import { useState, useCallback, useMemo } from 'react';
import {
  IconButton,
  Paper,
  Slider,
  Toolbar,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider,
  Collapse,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import TuneIcon from '@mui/icons-material/Tune';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import GradientIcon from '@mui/icons-material/Gradient';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import SpeedIcon from '@mui/icons-material/Speed';
import TerrainIcon from '@mui/icons-material/Terrain';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MapView from '../map/core/MapView';
import MapHeatLayer, { HEATMAP_COLOR_SCHEMES } from '../map/MapHeatLayer';
import { formatTime, formatSpeed, formatAltitude } from '../common/util/formatter';
import ReportFilter from '../reports/components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch } from '../reactHelper';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import StatusCard from '../common/components/StatusCard';
import MapScale from '../map/MapScale';
import BackIcon from '../common/components/BackIcon';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useAttributePreference } from '../common/util/preferences';
import { TOPBAR_HEIGHT } from '../common/components/TopBar';
import { GAP } from '../common/theme/layout';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
  },
  // Mapa em tela cheia (passa por baixo do TopBar transparente)
  mapContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    zIndex: 3,
    left: 0,
    top: TOPBAR_HEIGHT + GAP.SMALL, // TopBar + 4px spacing
    margin: theme.spacing(1.5),
    width: theme.dimensions.drawerWidthDesktop,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      margin: 0,
      top: TOPBAR_HEIGHT + GAP.SMALL,
    },
  },
  title: {
    flexGrow: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0.5),
    },
  },
  section: {
    marginBottom: theme.spacing(1),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(0.5, 0),
  },
  colorButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    '& .MuiSlider-root': {
      flex: 1,
    },
  },
  sliderLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    minWidth: 60,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  sliderValue: {
    fontSize: '0.7rem',
    color: theme.palette.text.primary,
    minWidth: 40,
    textAlign: 'right',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

/**
 * Configuração dos esquemas de cores
 */
const COLOR_SCHEME_CONFIG = {
  classic: { label: 'Clássico', icon: <GradientIcon fontSize="small" />, color: '#4CAF50' },
  fire: { label: 'Fogo', icon: <WhatshotIcon fontSize="small" />, color: '#FF5722' },
  cool: { label: 'Frio', icon: <AcUnitIcon fontSize="small" />, color: '#2196F3' },
  viridis: { label: 'Viridis', icon: <BlurOnIcon fontSize="small" />, color: '#9C27B0' },
  density: { label: 'Densidade', icon: <BlurOnIcon fontSize="small" />, color: '#E91E63' },
};

/**
 * Opções de peso do heatmap
 */
const WEIGHT_OPTIONS = [
  { value: '', label: 'Uniforme', icon: <GpsFixedIcon fontSize="small" /> },
  { value: 'speed', label: 'Velocidade', icon: <SpeedIcon fontSize="small" /> },
  { value: 'altitude', label: 'Altitude', icon: <TerrainIcon fontSize="small" /> },
];

/**
 * HeatmapPage - Página de mapa de calor standalone
 *
 * Similar ao ReplayPage, mas com visualização de heatmap.
 * Baseado nos mapas PHP:
 * - content_mapa_percurso_calor.php
 * - content_mapa_correlacao_calor.php
 */
const HeatmapPage = () => {
  const t = useTranslation();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  const [searchParams] = useSearchParams();
  const defaultDeviceId = useSelector((state) => state.devices.selectedId);
  const speedUnit = useAttributePreference('speedUnit');
  const altitudeUnit = useAttributePreference('altitudeUnit');

  // Estado dos dados
  const [positions, setPositions] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(defaultDeviceId);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [loading, setLoading] = useState(false);

  // Controles do heatmap
  const [radius, setRadius] = useState(25);
  const [intensity, setIntensity] = useState(1);
  const [opacity, setOpacity] = useState(0.8);
  const [colorScheme, setColorScheme] = useState('fire');
  const [weightProperty, setWeightProperty] = useState('');
  const [showPoints, setShowPoints] = useState(true);

  // Seção expansível de configurações
  const [settingsExpanded, setSettingsExpanded] = useState(true);

  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const loaded = Boolean(from && to && !loading && positions.length);

  // Nome do dispositivo
  const deviceName = useSelector((state) => {
    if (selectedDeviceId) {
      const device = state.devices.items[selectedDeviceId];
      if (device) {
        return device.name;
      }
    }
    return null;
  });

  // Estatísticas calculadas
  const stats = useMemo(() => {
    if (!positions.length) return null;

    const speeds = positions.map((p) => p.speed).filter((s) => s !== undefined && s !== null);
    const altitudes = positions.map((p) => p.altitude).filter((a) => a !== undefined && a !== null);

    return {
      totalPoints: positions.length,
      avgSpeed: speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0,
      maxSpeed: speeds.length ? Math.max(...speeds) : 0,
      minAltitude: altitudes.length ? Math.min(...altitudes) : 0,
      maxAltitude: altitudes.length ? Math.max(...altitudes) : 0,
      startTime: positions[0]?.fixTime,
      endTime: positions[positions.length - 1]?.fixTime,
    };
  }, [positions]);

  // Handler para buscar dados
  const handleShow = useCatch(async ({ deviceIds, from, to }) => {
    const deviceId = deviceIds.find(() => true);
    setLoading(true);
    setSelectedDeviceId(deviceId);
    setSelectedPosition(null);
    try {
      const query = new URLSearchParams({ deviceId, from, to });
      const response = await fetchOrThrow(`/api/positions?${query.toString()}`);
      const data = await response.json();
      setPositions(data);
      if (!data.length) {
        throw Error(t('sharedNoData'));
      }
    } finally {
      setLoading(false);
    }
  });

  // Handler para clique no ponto
  const handlePointClick = useCallback((position) => {
    setSelectedPosition(position);
    setShowCard(true);
  }, []);

  return (
    <div className={classes.root}>
      {/* Mapa em tela cheia - passa por baixo do TopBar transparente */}
      <div className={classes.mapContainer}>
        <MapView>
          <MapGeofence />
          {positions.length > 0 && (
            <MapHeatLayer
              positions={positions}
              radius={radius}
              intensity={intensity}
              opacity={opacity}
              colorScheme={colorScheme}
              weightProperty={weightProperty || null}
              showPoints={showPoints}
              onClick={handlePointClick}
            />
          )}
        </MapView>
        <MapScale />
        {positions.length > 0 && <MapCamera positions={positions} />}
      </div>

      {/* Sidebar */}
      <div
        className={classes.sidebar}
        style={{
          backgroundColor: tokens?.glassmorphism?.heavy?.background || 'rgba(13, 17, 23, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
          overflow: 'hidden',
        }}
      >
        <Paper elevation={0} square sx={{ backgroundColor: 'transparent' }}>
          <Toolbar>
            <IconButton edge="start" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" className={classes.title}>
              {t('reportHeatmap')}
            </Typography>
            {loaded && (
              <IconButton edge="end" onClick={() => setSettingsExpanded(!settingsExpanded)}>
                <TuneIcon />
              </IconButton>
            )}
          </Toolbar>
        </Paper>

        <Paper className={classes.content} square elevation={0} sx={{ backgroundColor: 'transparent', borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}` }}>
          {loaded ? (
            <>
              {/* Dispositivo e período */}
              <Box className={classes.section}>
                <Typography variant="subtitle1" align="center" gutterBottom>
                  {deviceName}
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" display="block">
                  {formatTime(from, 'minutes')} - {formatTime(to, 'minutes')}
                </Typography>
              </Box>

              <Divider />

              {/* Configurações do Heatmap */}
              <Box className={classes.section}>
                <Box
                  className={classes.sectionHeader}
                  onClick={() => setSettingsExpanded(!settingsExpanded)}
                >
                  <Typography variant="subtitle2">
                    {t('reportHeatmapSettings')}
                  </Typography>
                  {settingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                <Collapse in={settingsExpanded}>
                  {/* Esquema de Cores + Pontos */}
                  <Box className={classes.controlsRow}>
                    <ToggleButtonGroup
                      value={colorScheme}
                      exclusive
                      onChange={(e, val) => val && setColorScheme(val)}
                      size="small"
                    >
                      {HEATMAP_COLOR_SCHEMES.map((scheme) => (
                        <ToggleButton
                          key={scheme}
                          value={scheme}
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            '&.Mui-selected': {
                              bgcolor: `${COLOR_SCHEME_CONFIG[scheme]?.color}30`,
                              borderColor: COLOR_SCHEME_CONFIG[scheme]?.color,
                            },
                          }}
                        >
                          <Tooltip title={COLOR_SCHEME_CONFIG[scheme]?.label}>
                            {COLOR_SCHEME_CONFIG[scheme]?.icon}
                          </Tooltip>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Tooltip title={t('reportHeatmapShowPoints')}>
                      <ToggleButton
                        value="check"
                        selected={showPoints}
                        onChange={() => setShowPoints(!showPoints)}
                        size="small"
                        sx={{ p: 0.5 }}
                      >
                        <GpsFixedIcon fontSize="small" />
                      </ToggleButton>
                    </Tooltip>
                  </Box>

                  {/* Raio */}
                  <Box className={classes.sliderRow}>
                    <span className={classes.sliderLabel}>
                      <BlurOnIcon sx={{ fontSize: 14 }} />
                      {t('reportHeatmapRadius')}
                    </span>
                    <Slider
                      value={radius}
                      onChange={(e, val) => setRadius(val)}
                      min={5}
                      max={60}
                      step={5}
                      size="small"
                    />
                    <span className={classes.sliderValue}>{radius}px</span>
                  </Box>

                  {/* Intensidade */}
                  <Box className={classes.sliderRow}>
                    <span className={classes.sliderLabel}>
                      <WhatshotIcon sx={{ fontSize: 14 }} />
                      {t('reportHeatmapIntensity')}
                    </span>
                    <Slider
                      value={intensity}
                      onChange={(e, val) => setIntensity(val)}
                      min={0.1}
                      max={3}
                      step={0.1}
                      size="small"
                    />
                    <span className={classes.sliderValue}>{intensity.toFixed(1)}</span>
                  </Box>

                  {/* Opacidade */}
                  <Box className={classes.sliderRow}>
                    <span className={classes.sliderLabel}>
                      <GradientIcon sx={{ fontSize: 14 }} />
                      {t('reportHeatmapOpacity')}
                    </span>
                    <Slider
                      value={opacity}
                      onChange={(e, val) => setOpacity(val)}
                      min={0.1}
                      max={1}
                      step={0.1}
                      size="small"
                    />
                    <span className={classes.sliderValue}>{Math.round(opacity * 100)}%</span>
                  </Box>

                  {/* Peso */}
                  <Box className={classes.controlsRow} sx={{ mt: 0.5 }}>
                    <span className={classes.sliderLabel}>{t('reportHeatmapWeight')}</span>
                    <ToggleButtonGroup
                      value={weightProperty}
                      exclusive
                      onChange={(e, val) => setWeightProperty(val ?? '')}
                      size="small"
                    >
                      {WEIGHT_OPTIONS.map((opt) => (
                        <ToggleButton key={opt.value} value={opt.value} sx={{ px: 1, py: 0.5 }}>
                          <Tooltip title={opt.label}>
                            {opt.icon}
                          </Tooltip>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                </Collapse>
              </Box>

              <Divider />

              {/* Estatísticas */}
              {stats && (
                <Box className={classes.stats} sx={{ mt: 1 }}>
                  <Tooltip title="Total de pontos">
                    <Box className={classes.statItem}>
                      <GpsFixedIcon sx={{ fontSize: 12 }} />
                      <span>{stats.totalPoints.toLocaleString()}</span>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Velocidade média">
                    <Box className={classes.statItem}>
                      <SpeedIcon sx={{ fontSize: 12 }} />
                      <span>{formatSpeed(stats.avgSpeed, speedUnit, t)}</span>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Velocidade máxima">
                    <Box className={classes.statItem}>
                      <SpeedIcon sx={{ fontSize: 12, color: 'error.main' }} />
                      <span>{formatSpeed(stats.maxSpeed, speedUnit, t)}</span>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Altitude (min-max)">
                    <Box className={classes.statItem}>
                      <TerrainIcon sx={{ fontSize: 12 }} />
                      <span>{formatAltitude(stats.minAltitude, altitudeUnit, t)}-{formatAltitude(stats.maxAltitude, altitudeUnit, t)}</span>
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </>
          ) : (
            <ReportFilter onShow={handleShow} deviceType="single" loading={loading} compact />
          )}
        </Paper>
      </div>

      {/* Status Card */}
      {showCard && selectedPosition && (
        <StatusCard
          deviceId={selectedDeviceId}
          position={selectedPosition}
          onClose={() => setShowCard(false)}
          disableActions
        />
      )}
    </div>
  );
};

export default HeatmapPage;
